import React, { useState } from 'react';
import { Calendar, Heart, Users, Settings as SettingsIcon, Save, AlertCircle, Loader2 } from 'lucide-react';
import { useNotificationData } from './useNotificationData';
import {
  NOTIFICATION_CATEGORIES,
  STAFF_ROLES,
  REMINDER_TIMING_OPTIONS,
  NotificationSetting,
} from './notificationConstants';
import NotificationCard from './NotificationCard';
import ChannelToggles from './ChannelToggles';

const CATEGORY_ICONS: Record<string, React.FC<any>> = {
  calendar: Calendar,
  heart: Heart,
  users: Users,
};

const ChannelTogglesInline = ChannelToggles;

const CUSTOMER_APPOINTMENT_ORDER = [
  'appointment_confirmation',
  'appointment_reminder',
  'appointment_changed',
  'appointment_cancellation',
];

const STAFF_ORDER = [
  'staff_new_appointment',
  'staff_appointment_rescheduled',
  'staff_appointment_cancelled',
];

const sortByOrder = (items: NotificationSetting[], order: string[]): NotificationSetting[] =>
  [...items].sort((a, b) => order.indexOf(a.notification_key) - order.indexOf(b.notification_key));

const CUSTOMER_FOLLOWUP_ORDER = [
  'post_appointment_thank_you',
  'rebooking_reminder',
  'review_request',
];

const FOLLOWUP_TIMING_OPTIONS = [
  { value: 0, label: 'Immediately after appointment' },
  { value: 60, label: '1 hour after' },
  { value: 120, label: '2 hours after' },
  { value: 240, label: '4 hours after' },
  { value: 720, label: '12 hours after' },
  { value: 1440, label: '24 hours (1 day) after' },
  { value: 2880, label: '2 days after' },
  { value: 4320, label: '3 days after' },
];

const REBOOKING_TIMING_OPTIONS = [
  { value: 10080, label: '1 week after' },
  { value: 20160, label: '2 weeks after' },
  { value: 30240, label: '3 weeks after' },
  { value: 40320, label: '4 weeks after' },
  { value: 60480, label: '6 weeks after' },
  { value: 80640, label: '8 weeks after' },
  { value: 120960, label: '12 weeks after' },
];

export const NotificationSettings: React.FC = () => {
  const {
    settings,
    reminders,
    loading,
    saving,
    error,
    updateSetting,
    addReminder,
    removeReminder,
    updateReminder,
    saveAll,
  } = useNotificationData();

  const [activeTab, setActiveTab] = useState('customer_appointment');
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});

  const toggleExpand = (id: string) => {
    setExpandedCards((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getRemindersForSetting = (settingId: string) => reminders.filter((r) => r.setting_id === settingId);

  const getSettingsByCategory = (category: string): NotificationSetting[] =>
    settings.filter((s) => s.category === category);

  const toggleRole = (settingId: string, role: string) => {
    const setting = settings.find((s) => s.id === settingId);
    if (!setting) return;
    const roles = setting.applicable_roles.includes(role)
      ? setting.applicable_roles.filter((r) => r !== role)
      : [...setting.applicable_roles, role];
    updateSetting(settingId, { applicable_roles: roles });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
        <span className="ml-3 text-gray-500">Loading notification settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Notifications</h2>
        <p className="text-sm text-gray-500 mt-1">Manage when and how Vara communicates with your customers and team.</p>
      </div>

      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-600 mt-0.5">{error}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <nav className="flex gap-1 min-w-max">
          {NOTIFICATION_CATEGORIES.map((cat) => {
            const Icon = CATEGORY_ICONS[cat.icon] || SettingsIcon;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === cat.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{cat.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="space-y-4">
        {/* Customer Appointment Notifications */}
        {activeTab === 'customer_appointment' && (
          <div className="space-y-4">
            {sortByOrder(getSettingsByCategory('customer_appointment'), CUSTOMER_APPOINTMENT_ORDER).map((setting) => {
              const isReminder = setting.notification_key === 'appointment_reminder';
              const settingReminders = isReminder ? getRemindersForSetting(setting.id) : [];
              return (
                <NotificationCard
                  key={setting.id}
                  setting={setting}
                  expanded={expandedCards[setting.id] || false}
                  onToggleExpand={() => toggleExpand(setting.id)}
                  onToggleEnabled={(enabled) => updateSetting(setting.id, { enabled })}
                  onToggleSms={(enabled) => updateSetting(setting.id, { sms_enabled: enabled })}
                  onToggleEmail={(enabled) => updateSetting(setting.id, { email_enabled: enabled })}
                  onTimingChange={(minutes) => updateSetting(setting.id, { timing_offset_minutes: minutes })}
                  showTiming={false}
                >
                  {isReminder && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-sm font-medium text-gray-900">Reminder Schedule</h5>
                        <span className="text-xs text-gray-500">{settingReminders.length} reminder{settingReminders.length !== 1 ? 's' : ''} configured</span>
                      </div>

                      {/* Frequency */}
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                        <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">Frequency</label>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { value: 1, label: 'Once' },
                            { value: 2, label: 'Twice' },
                            { value: 3, label: '3 times' },
                          ].map((freq) => (
                            <button
                              key={freq.value}
                              onClick={() => {
                                const currentCount = settingReminders.length;
                                if (freq.value > currentCount) {
                                  for (let i = 0; i < freq.value - currentCount; i++) {
                                    addReminder(setting.id);
                                  }
                                } else if (freq.value < currentCount) {
                                  for (let i = 0; i < currentCount - freq.value; i++) {
                                    const last = settingReminders[settingReminders.length - 1 - i];
                                    if (last) removeReminder(last.id);
                                  }
                                }
                              }}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                settingReminders.length === freq.value
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                              }`}
                            >
                              {freq.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Timing rows */}
                      <div className="space-y-2">
                        {settingReminders.map((reminder, idx) => (
                          <div key={reminder.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 flex-wrap">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-500">Reminder {idx + 1}</span>
                            </div>
                            <span className="text-sm text-gray-400">Send</span>
                            <select
                              value={reminder.offset_minutes}
                              onChange={(e) => updateReminder(reminder.id, { offset_minutes: parseInt(e.target.value) })}
                              className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                            >
                              {REMINDER_TIMING_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                            <span className="text-sm text-gray-400">via</span>
                            <ChannelTogglesInline
                              smsEnabled={reminder.sms_enabled}
                              emailEnabled={reminder.email_enabled}
                              onSmsChange={(enabled) => updateReminder(reminder.id, { sms_enabled: enabled })}
                              onEmailChange={(enabled) => updateReminder(reminder.id, { email_enabled: enabled })}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </NotificationCard>
              );
            })}
          </div>
        )}

        {/* Customer Follow-Up Notifications */}
        {activeTab === 'customer_followup' && (
          <div className="space-y-4">
            {sortByOrder(getSettingsByCategory('customer_followup'), CUSTOMER_FOLLOWUP_ORDER).map((setting) => {
              const isRebooking = setting.notification_key === 'rebooking_reminder';
              const isReview = setting.notification_key === 'review_request';
              const timingOptions = isRebooking ? REBOOKING_TIMING_OPTIONS : FOLLOWUP_TIMING_OPTIONS;
              return (
                <NotificationCard
                  key={setting.id}
                  setting={setting}
                  expanded={expandedCards[setting.id] || false}
                  onToggleExpand={() => toggleExpand(setting.id)}
                  onToggleEnabled={(enabled) => updateSetting(setting.id, { enabled })}
                  onToggleSms={(enabled) => updateSetting(setting.id, { sms_enabled: enabled })}
                  onToggleEmail={(enabled) => updateSetting(setting.id, { email_enabled: enabled })}
                  onTimingChange={(minutes) => updateSetting(setting.id, { timing_offset_minutes: minutes })}
                  timingOptions={timingOptions}
                  showTiming={true}
                >
                  {isReview && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Review URL</label>
                        <input
                          type="url"
                          value={setting.extra_config?.review_url || ''}
                          onChange={(e) => updateSetting(setting.id, { extra_config: { ...setting.extra_config, review_url: e.target.value } })}
                          placeholder="https://reviews.google.com/your-business"
                          className="w-full max-w-lg px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">The link customers will be directed to leave a review.</p>
                      </div>
                    </div>
                  )}
                </NotificationCard>
              );
            })}
          </div>
        )}

        {/* Staff Notifications */}
        {activeTab === 'staff' && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Staff Notifications</p>
                  <p className="text-sm text-blue-700 mt-0.5">
                    Configure how your team receives internal notifications. Each type can be delivered via SMS, email, or in-app, and sent to specific roles.
                  </p>
                </div>
              </div>
            </div>
            {sortByOrder(
              getSettingsByCategory('staff')
                .filter((s) => STAFF_ORDER.includes(s.notification_key)),
              STAFF_ORDER
            ).map((setting) => (
              <NotificationCard
                key={setting.id}
                setting={setting}
                expanded={expandedCards[setting.id] || false}
                onToggleExpand={() => toggleExpand(setting.id)}
                onToggleEnabled={(enabled) => updateSetting(setting.id, { enabled })}
                onToggleSms={(enabled) => updateSetting(setting.id, { sms_enabled: enabled })}
                onToggleEmail={(enabled) => updateSetting(setting.id, { email_enabled: enabled })}
                onTimingChange={() => {}}
                showTiming={false}
              >
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Send To</label>
                    <div className="flex flex-wrap gap-2">
                      {STAFF_ROLES.filter((r) => r.value !== 'selected').map((role) => (
                        <button
                          key={role.value}
                          onClick={() => toggleRole(setting.id, role.value)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                            setting.applicable_roles.includes(role.value)
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                          }`}
                        >
                          {role.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                    <span className="text-xs text-gray-500">In-app notifications are always enabled for selected roles.</span>
                  </div>
                </div>
              </NotificationCard>
            ))}
          </div>
        )}
      </div>

      {/* Save Bar */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200 sticky bottom-0 bg-white py-4 -mx-6 px-6 z-10">
        <p className="text-sm text-gray-500">
          {saving ? 'Saving...' : 'Changes are saved to your notification configuration.'}
        </p>
        <button
          onClick={saveAll}
          disabled={saving}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium cursor-pointer"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          <span>{saving ? 'Saving...' : 'Save Changes'}</span>
        </button>
      </div>
    </div>
  );
};

export default NotificationSettings;
